import { PRODUCTS, getProduct, computeLineValue } from '../shared/products.js';
import { STAGES, LATERAL_STATES } from '../shared/orderModel.js';

// Point 4: ONE fixed clinic, no switcher. Point 7: deliberately does NOT
// include category / territory / potential score / business-profiling
// fields — those are Hetero's internal classification of the pharmacy, not
// something the pharmacy should see about itself. Only what the BRD's
// "Pharmacy Details" section actually lists as pharmacy-facing.
export const MY_PHARMACY = {
  customerId: 'PH-Z-0002',
  pharmacyName: 'CityCare Chemist',
  ownerName: 'S. Ncube',
  mobile1: '+263 77 234 5678',
  mobile2: '+263 78 991 2233',
  email1: 'citycare.chemist@pharmzw.co.zw',
  email2: null,
  drugLicenseNo: 'MCAZ-1007-2024',
  address: '14 Josiah Chinamano Ave',
  city: 'Chitungwiza',
  state: 'Harare Province',
  pincode: '263011',
  assignedSalesExec: 'Tendai Moyo',
};

function lineItem(sku, quantity) {
  const p = getProduct(sku);
  const { value, schemeApplied, freeQuantity, totalQuantity } = computeLineValue(sku, quantity);
  return { productSku: sku, productName: p.productName, quantity, unitPrice: p.unitPrice, schemeApplied, freeQuantity, totalQuantity, lineValue: value };
}

function history(stagesWithDates) {
  return stagesWithDates.map(([stage, ts]) => ({ stage, timestamp: ts }));
}

// 20 orders. Deliberately hand-authored (not randomly generated) so every
// state this role's screens need is guaranteed present: 2 Pending (awaiting
// Confirm/Cancel), a full spread across the other 6 in-flight stages,
// several Delivered with mixed billing stages (incl. one Payment Reported —
// "settled outside the system" per point 11), 2 Cancelled, 1 Returned for
// Modification (informational for the pharmacy, actionable for Sales Exec).
export const MY_ORDERS = [
  {
    orderNo: 'ORD-P-001', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-07-28',
    lineItems: [lineItem('HET-1001', 20), lineItem('HET-5001', 11)],
    currentStage: 'Order Received',
    statusHistory: history([['Order Received', '2026-07-28T09:12:00']]),
    comments: [],
    invoice: null, // no billing entry until pharmacy confirms
  },
  {
    orderNo: 'ORD-P-002', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-07-28',
    lineItems: [lineItem('HET-2001', 30)],
    currentStage: 'Order Received',
    statusHistory: history([['Order Received', '2026-07-28T14:30:00']]),
    comments: [],
    invoice: null,
  },
  {
    orderNo: 'ORD-P-003', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-07-26',
    lineItems: [lineItem('HET-3001', 22), lineItem('HET-4001', 40)],
    currentStage: 'Telecaller Validation',
    statusHistory: history([
      ['Order Received', '2026-07-26T08:00:00'],
      ['Telecaller Validation', '2026-07-26T08:40:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: null, invoiceDate: null, baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Order Confirmed' },
  },
  {
    orderNo: 'ORD-P-004', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-07-25',
    lineItems: [lineItem('HET-1003', 33)],
    currentStage: 'Stock Allocation',
    statusHistory: history([
      ['Order Received', '2026-07-25T09:00:00'],
      ['Telecaller Validation', '2026-07-25T09:35:00'],
      ['Stock Allocation', '2026-07-25T11:00:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: null, invoiceDate: null, baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Order Confirmed' },
  },
  {
    orderNo: 'ORD-P-005', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-07-24',
    lineItems: [lineItem('HET-2002', 22), lineItem('HET-5002', 22)],
    currentStage: 'Warehouse Processing',
    statusHistory: history([
      ['Order Received', '2026-07-24T08:15:00'],
      ['Telecaller Validation', '2026-07-24T08:50:00'],
      ['Stock Allocation', '2026-07-24T10:20:00'],
      ['Warehouse Processing', '2026-07-24T13:00:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: null, invoiceDate: null, baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Order Confirmed' },
  },
  {
    orderNo: 'ORD-P-006', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-07-22',
    lineItems: [lineItem('HET-1002', 18)],
    currentStage: 'Dispatch Pending',
    statusHistory: history([
      ['Order Received', '2026-07-22T09:00:00'],
      ['Telecaller Validation', '2026-07-22T09:40:00'],
      ['Stock Allocation', '2026-07-22T11:15:00'],
      ['Warehouse Processing', '2026-07-22T14:00:00'],
      ['Dispatch Pending', '2026-07-23T08:30:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: null, invoiceDate: null, baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Order Confirmed' },
  },
  {
    orderNo: 'ORD-P-007', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-07-20',
    lineItems: [lineItem('HET-3002', 15), lineItem('HET-4002', 12)],
    currentStage: 'Dispatched',
    statusHistory: history([
      ['Order Received', '2026-07-20T08:00:00'],
      ['Telecaller Validation', '2026-07-20T08:45:00'],
      ['Stock Allocation', '2026-07-20T10:30:00'],
      ['Warehouse Processing', '2026-07-20T13:15:00'],
      ['Dispatch Pending', '2026-07-21T08:00:00'],
      ['Dispatched', '2026-07-21T15:20:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: null, invoiceDate: null, baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Invoice Pending' },
  },
  {
    orderNo: 'ORD-P-008', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-07-14',
    lineItems: [lineItem('HET-1001', 22), lineItem('HET-2001', 20)],
    currentStage: 'Delivered',
    statusHistory: history([
      ['Order Received', '2026-07-14T08:00:00'], ['Telecaller Validation', '2026-07-14T08:40:00'],
      ['Stock Allocation', '2026-07-14T10:00:00'], ['Warehouse Processing', '2026-07-14T13:00:00'],
      ['Dispatch Pending', '2026-07-15T08:00:00'], ['Dispatched', '2026-07-15T14:00:00'],
      ['Invoice Generated', '2026-07-16T09:00:00'], ['Delivered', '2026-07-17T11:30:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: 'INV-P-1008', invoiceDate: '2026-07-16', baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Payment Received', paymentMethod: 'Platform Payment', paymentDate: '2026-07-18' },
  },
  {
    orderNo: 'ORD-P-009', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-07-10',
    lineItems: [lineItem('HET-5001', 33), lineItem('HET-5002', 11)],
    currentStage: 'Delivered',
    statusHistory: history([
      ['Order Received', '2026-07-10T08:00:00'], ['Telecaller Validation', '2026-07-10T08:40:00'],
      ['Stock Allocation', '2026-07-10T10:00:00'], ['Warehouse Processing', '2026-07-10T13:00:00'],
      ['Dispatch Pending', '2026-07-11T08:00:00'], ['Dispatched', '2026-07-11T14:00:00'],
      ['Invoice Generated', '2026-07-12T09:00:00'], ['Delivered', '2026-07-13T10:15:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: 'INV-P-1009', invoiceDate: '2026-07-12', baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Payment Pending' },
  },
  {
    orderNo: 'ORD-P-010', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-07-08',
    lineItems: [lineItem('HET-2002', 14)],
    currentStage: 'Delivered',
    statusHistory: history([
      ['Order Received', '2026-07-08T08:00:00'], ['Telecaller Validation', '2026-07-08T08:40:00'],
      ['Stock Allocation', '2026-07-08T10:00:00'], ['Warehouse Processing', '2026-07-08T13:00:00'],
      ['Dispatch Pending', '2026-07-09T08:00:00'], ['Dispatched', '2026-07-09T14:00:00'],
      ['Invoice Generated', '2026-07-10T09:00:00'], ['Delivered', '2026-07-11T10:00:00'],
    ]),
    comments: [],
    // "settled outside the system" — point 11's second payment path
    invoice: { invoiceNo: 'INV-P-1010', invoiceDate: '2026-07-10', baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Payment Received', paymentMethod: 'Recorded (settled outside the platform)', paymentDate: '2026-07-14' },
  },
  {
    orderNo: 'ORD-P-011', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-06-30',
    lineItems: [lineItem('HET-1003', 22), lineItem('HET-3001', 33)],
    currentStage: 'Delivered',
    statusHistory: history([
      ['Order Received', '2026-06-30T08:00:00'], ['Telecaller Validation', '2026-06-30T08:40:00'],
      ['Stock Allocation', '2026-06-30T10:00:00'], ['Warehouse Processing', '2026-06-30T13:00:00'],
      ['Dispatch Pending', '2026-07-01T08:00:00'], ['Dispatched', '2026-07-01T14:00:00'],
      ['Invoice Generated', '2026-07-02T09:00:00'], ['Delivered', '2026-07-03T10:00:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: 'INV-P-1011', invoiceDate: '2026-07-02', baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Payment Received', paymentMethod: 'Platform Payment', paymentDate: '2026-07-05' },
  },
  {
    orderNo: 'ORD-P-012', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-06-20',
    lineItems: [lineItem('HET-4001', 44)],
    currentStage: 'Delivered',
    statusHistory: history([
      ['Order Received', '2026-06-20T08:00:00'], ['Telecaller Validation', '2026-06-20T08:40:00'],
      ['Stock Allocation', '2026-06-20T10:00:00'], ['Warehouse Processing', '2026-06-20T13:00:00'],
      ['Dispatch Pending', '2026-06-21T08:00:00'], ['Dispatched', '2026-06-21T14:00:00'],
      ['Invoice Generated', '2026-06-22T09:00:00'], ['Delivered', '2026-06-23T10:00:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: 'INV-P-1012', invoiceDate: '2026-06-22', baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Payment Received', paymentMethod: 'Platform Payment', paymentDate: '2026-06-25' },
  },
  {
    orderNo: 'ORD-P-013', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-06-10',
    lineItems: [lineItem('HET-5002', 33), lineItem('HET-1002', 10)],
    currentStage: 'Delivered',
    statusHistory: history([
      ['Order Received', '2026-06-10T08:00:00'], ['Telecaller Validation', '2026-06-10T08:40:00'],
      ['Stock Allocation', '2026-06-10T10:00:00'], ['Warehouse Processing', '2026-06-10T13:00:00'],
      ['Dispatch Pending', '2026-06-11T08:00:00'], ['Dispatched', '2026-06-11T14:00:00'],
      ['Invoice Generated', '2026-06-12T09:00:00'], ['Delivered', '2026-06-13T10:00:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: 'INV-P-1013', invoiceDate: '2026-06-12', baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Payment Received', paymentMethod: 'Platform Payment', paymentDate: '2026-06-15' },
  },
  {
    orderNo: 'ORD-P-014', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-07-18',
    lineItems: [lineItem('HET-2001', 15)],
    currentStage: LATERAL_STATES.CANCELLED,
    statusHistory: history([
      ['Order Received', '2026-07-18T09:00:00'],
    ]),
    cancelledBy: 'pharmacy', cancelReason: 'Ordered in error — duplicate of ORD-P-003',
    comments: [],
    invoice: null,
  },
  {
    orderNo: 'ORD-P-015', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-07-05',
    lineItems: [lineItem('HET-3002', 8)],
    currentStage: LATERAL_STATES.CANCELLED,
    statusHistory: history([
      ['Order Received', '2026-07-05T08:00:00'],
      ['Telecaller Validation', '2026-07-05T08:30:00'],
    ]),
    cancelledBy: 'telecaller', cancelReason: 'Stock unavailable at time of validation',
    comments: [],
    invoice: null,
  },
  {
    orderNo: 'ORD-P-016', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-07-27',
    lineItems: [lineItem('HET-1001', 15), lineItem('HET-4001', 20)],
    currentStage: LATERAL_STATES.RETURNED,
    statusHistory: history([
      ['Order Received', '2026-07-27T09:00:00'],
      ['Telecaller Validation', '2026-07-27T09:30:00'],
    ]),
    // Informational for the pharmacy — the bounce-back is actioned by Sales
    // Exec, but the pharmacy can still see why their order is paused.
    comments: [
      { from: 'Telecaller — R. Gumbo', timestamp: '2026-07-27T10:15:00', text: 'Quantity for HET-1001 exceeds standard order limit for this pharmacy tier — please confirm with the pharmacy or adjust.' },
    ],
    invoice: null,
  },
  {
    orderNo: 'ORD-P-017', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-05-28',
    lineItems: [lineItem('HET-2002', 22)],
    currentStage: 'Delivered',
    statusHistory: history([
      ['Order Received', '2026-05-28T08:00:00'], ['Telecaller Validation', '2026-05-28T08:40:00'],
      ['Stock Allocation', '2026-05-28T10:00:00'], ['Warehouse Processing', '2026-05-28T13:00:00'],
      ['Dispatch Pending', '2026-05-29T08:00:00'], ['Dispatched', '2026-05-29T14:00:00'],
      ['Invoice Generated', '2026-05-30T09:00:00'], ['Delivered', '2026-05-31T10:00:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: 'INV-P-1017', invoiceDate: '2026-05-30', baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Payment Received', paymentMethod: 'Platform Payment', paymentDate: '2026-06-02' },
  },
  {
    orderNo: 'ORD-P-018', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-05-15',
    lineItems: [lineItem('HET-5001', 22), lineItem('HET-3001', 11)],
    currentStage: 'Delivered',
    statusHistory: history([
      ['Order Received', '2026-05-15T08:00:00'], ['Telecaller Validation', '2026-05-15T08:40:00'],
      ['Stock Allocation', '2026-05-15T10:00:00'], ['Warehouse Processing', '2026-05-15T13:00:00'],
      ['Dispatch Pending', '2026-05-16T08:00:00'], ['Dispatched', '2026-05-16T14:00:00'],
      ['Invoice Generated', '2026-05-17T09:00:00'], ['Delivered', '2026-05-18T10:00:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: 'INV-P-1018', invoiceDate: '2026-05-17', baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Payment Pending' },
  },
  {
    orderNo: 'ORD-P-019', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-04-20',
    lineItems: [lineItem('HET-1002', 12), lineItem('HET-4002', 10)],
    currentStage: 'Delivered',
    statusHistory: history([
      ['Order Received', '2026-04-20T08:00:00'], ['Telecaller Validation', '2026-04-20T08:40:00'],
      ['Stock Allocation', '2026-04-20T10:00:00'], ['Warehouse Processing', '2026-04-20T13:00:00'],
      ['Dispatch Pending', '2026-04-21T08:00:00'], ['Dispatched', '2026-04-21T14:00:00'],
      ['Invoice Generated', '2026-04-22T09:00:00'], ['Delivered', '2026-04-23T10:00:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: 'INV-P-1019', invoiceDate: '2026-04-22', baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Payment Received', paymentMethod: 'Platform Payment', paymentDate: '2026-04-25' },
  },
  {
    orderNo: 'ORD-P-020', customerId: MY_PHARMACY.customerId,
    orderDate: '2026-04-05',
    lineItems: [lineItem('HET-2001', 25)],
    currentStage: 'Delivered',
    statusHistory: history([
      ['Order Received', '2026-04-05T08:00:00'], ['Telecaller Validation', '2026-04-05T08:40:00'],
      ['Stock Allocation', '2026-04-05T10:00:00'], ['Warehouse Processing', '2026-04-05T13:00:00'],
      ['Dispatch Pending', '2026-04-06T08:00:00'], ['Dispatched', '2026-04-06T14:00:00'],
      ['Invoice Generated', '2026-04-07T09:00:00'], ['Delivered', '2026-04-08T10:00:00'],
    ]),
    comments: [],
    invoice: { invoiceNo: 'INV-P-1020', invoiceDate: '2026-04-07', baseValue: 0, taxAmount: 0, invoiceValue: 0, billingStage: 'Payment Received', paymentMethod: 'Platform Payment', paymentDate: '2026-04-10' },
  },
];

// Zimbabwe VAT 15% — fills in the invoice sub-object's tax/value fields from
// each order's actual line-item total, computed once at module load rather
// than hand-typed (hand-typing 20 correct VAT calcs invites arithmetic slips).
const TAX_RATE = 0.15;
MY_ORDERS.forEach(o => {
  if (!o.invoice) return;
  const base = totalOfLineItems(o.lineItems);
  o.invoice.baseValue = base;
  o.invoice.taxAmount = Math.round(base * TAX_RATE * 100) / 100;
  o.invoice.invoiceValue = Math.round((base + o.invoice.taxAmount) * 100) / 100;
});
function totalOfLineItems(items) {
  return Math.round(items.reduce((s, li) => s + li.lineValue, 0) * 100) / 100;
}
MY_ORDERS.forEach(o => { o.orderValue = totalOfLineItems(o.lineItems); });

// Simplified promotions for the pharmacy view (point 12) — image + short
// offer description, not the marketer's reach/effectiveness/adoption analytics.
export const MY_PROMOTIONS = [
  { id: 'PROMO-1', productSku: 'HET-2001', productName: 'Cardiopril 10', title: 'Heart Health Drive', imageUrl: '/promo-cardiopril.jpg', description: '10+1 free on Cardiopril 10 — now through end of August.', validUntil: '2026-08-31' },
  { id: 'PROMO-2', productSku: 'HET-1001', productName: 'Hetrocef 500', title: 'Antibiotic Restock Offer', imageUrl: '/promo-hetrocef.jpg', description: 'Order 11+ packs of Hetrocef 500 and the 11th is free.', validUntil: '2026-08-15' },
  { id: 'PROMO-3', productSku: 'HET-5001', productName: 'Vitahet C 1000', title: 'Immunity Season', imageUrl: '/promo-vitahet.jpg', description: 'Stock up on Vitahet C ahead of the winter season — 10+1 on all orders.', validUntil: '2026-09-10' },
];

// Notifications (point 13): Order + Invoice only, Stock notifications are
// never shown to a pharmacy — they have no operational meaning to a payer.
export const MY_NOTIFICATIONS = [
  { id: 'N-P-1', type: 'Order', message: 'Order ORD-P-007 has been dispatched', timestamp: '2026-07-21T15:20:00', relatedOrderNo: 'ORD-P-007' },
  { id: 'N-P-2', type: 'Order', message: 'Order ORD-P-016 was returned for modification by Telecaller', timestamp: '2026-07-27T10:15:00', relatedOrderNo: 'ORD-P-016' },
  { id: 'N-P-3', type: 'Invoice', message: 'Invoice INV-P-1018 is pending payment', timestamp: '2026-05-17T09:00:00', relatedOrderNo: 'ORD-P-018' },
  { id: 'N-P-4', type: 'Invoice', message: 'Invoice INV-P-1009 is pending payment', timestamp: '2026-07-12T09:00:00', relatedOrderNo: 'ORD-P-009' },
  { id: 'N-P-5', type: 'Order', message: 'Order ORD-P-014 was cancelled', timestamp: '2026-07-18T09:05:00', relatedOrderNo: 'ORD-P-014' },
];
